import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";

import MenuListItem from "@/app/AppShellMenuListItem.jsx";
import CheckIcon from "@/inline-assets/check-small.svg";
import DropDownIcon from "@/inline-assets/dropdown1.svg";
import { classNames } from "@/util/class-names.mjs";
import useDropdownRef from "@/util/use-dropdown-ref.mjs";

const DropdownWrapper = styled("label", React.forwardRef)`
  position: relative;

  ul.dropdown {
    position: absolute;

    opacity: 1;

    transform: translateY(0px);

    transition:
      opacity var(--transition),
      transform var(--transition);

    z-index: 2;
  }

  & > input:not(:checked) ~ ul.dropdown {
    visibility: hidden;

    opacity: 0;

    transform: translateY(calc(var(--sp-3) * -1));
  }

  div.button {
    display: flex;
    padding: var(--sp-1_5) var(--sp-1_5) var(--sp-1_5) var(--sp-2_5);
    justify-content: center;
    align-items: center;
    gap: var(--sp-2);
    border-radius: var(--br);
    cursor: pointer;

    color: var(--shade0-50);
  }

  &:hover {
    .button {
      color: var(--shade0-75);
    }
  }

  ul.dropdown {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--sp-0_5);

    padding: var(--sp-1);

    background: var(--shade7);
    border-radius: var(--sp-2);
    border: 1px solid var(--shade6-50);
    box-shadow:
      0px 12px 24px 0px rgba(7, 8, 12, 0.25),
      0px 4px 12px 0px rgba(7, 8, 12, 0.25);
  }
`;

export default function SelectDropdown({
  selected,
  options,
  onChange,
}: {
  selected: string;
  options: { value: string; text: Translation }[];
  onChange: (value: string) => void;
}) {
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const closeDropdown = useCallback(() => {
    setDropdownVisible(false);
  }, []);

  const ref = useDropdownRef<HTMLLabelElement>({
    onClickOutside: closeDropdown,
    onWindowResize: closeDropdown,
    onAssignNode: () => {},
  });

  const onToggleDropdown = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDropdownVisible(event.target.checked);
    },
    [],
  );

  const { t } = useTranslation();

  const selectedOption =
    options.find((o) => o.value === selected) || options[0];

  return (
    <DropdownWrapper ref={ref}>
      <input
        type="checkbox"
        className="visually-hidden"
        onChange={onToggleDropdown}
        checked={dropdownVisible}
      />

      <div className="button">
        <span className="type-callout--semi">{t(...selectedOption?.text)}</span>
        <DropDownIcon />
      </div>

      <ul className="dropdown">
        {options.map((option) => (
          <MenuListItem
            key={option.value}
            text={option.text}
            onClick={() => onChange(option.value)}
            Icon={option.value === selected && <CheckIcon />}
            {...classNames(
              option.value === selected
                ? "active type-callout--semi"
                : "type-callout",
            )}
          />
        ))}
      </ul>
    </DropdownWrapper>
  );
}
